import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Truck, ShoppingBag, MapPin, Clock, AlertTriangle, CalendarClock } from "lucide-react";
import api from "@/lib/api";

const formatDateLabel = (iso) => {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map((v) => parseInt(v, 10));
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long" });
  } catch {
    return iso;
  }
};

const OrderContextDialog = ({
  open,
  onClose,
  initial,
  settings,
  onConfirm,
}) => {
  const [orderType, setOrderType] = useState(initial?.orderType || "delivery");
  const [postalCode, setPostalCode] = useState(initial?.postalCode || "");
  const [scheduledTime, setScheduledTime] = useState(initial?.scheduledTime || "");
  const [scheduledDate, setScheduledDate] = useState(initial?.scheduledDate || "");
  const [isNextDay, setIsNextDay] = useState(!!initial?.isNextDay);
  const [todaySlots, setTodaySlots] = useState([]);
  const [nextDay, setNextDay] = useState(null); // { date, weekday, slots }
  const [closedInfo, setClosedInfo] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSlots(true);
      try {
        const { data } = await api.get(`/timeslots?order_type=${orderType}`);
        if (cancelled) return;
        setTodaySlots(data.slots || []);
        setNextDay(data.next_day || null);
        setClosedInfo({
          is_closed_today: data.is_closed_today,
          today_windows: data.today_windows || [],
          accepting_orders: data.accepting_orders,
          today_date: data.today_date,
        });

        // Choose appropriate default slot
        const todayList = data.slots || [];
        const nd = data.next_day || null;
        if (todayList.length > 0) {
          if (!todayList.includes(scheduledTime)) {
            setScheduledTime(todayList[0]);
            setScheduledDate("");
            setIsNextDay(false);
          } else {
            setIsNextDay(false);
            setScheduledDate("");
          }
        } else if (nd && nd.slots && nd.slots.length) {
          if (!nd.slots.includes(scheduledTime) || scheduledDate !== nd.date) {
            setScheduledTime(nd.slots[0]);
            setScheduledDate(nd.date);
            setIsNextDay(true);
          } else {
            setIsNextDay(true);
            setScheduledDate(nd.date);
          }
        } else {
          setScheduledTime("");
          setScheduledDate("");
          setIsNextDay(false);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderType, open]);

  const postalEntry = useMemo(() => {
    if (!settings || !postalCode) return null;
    const list = Array.isArray(settings.postal_codes) ? settings.postal_codes : [];
    return list.find((p) => String(p.code).trim() === postalCode.trim()) || null;
  }, [settings, postalCode]);

  const minOrder = postalEntry ? Number(postalEntry.min_order) : null;
  const deliveryFee = postalEntry ? Number(postalEntry.delivery_fee) : null;
  const postalInvalid =
    orderType === "delivery" && /^\d{4}$/.test(postalCode) && !postalEntry;

  const hasToday = todaySlots.length > 0;
  const hasNextDay = !!(nextDay && nextDay.slots && nextDay.slots.length > 0);
  const acceptingOrders = !!(closedInfo && closedInfo.accepting_orders);
  const noSlotsAtAll = !hasToday && !hasNextDay;
  const restaurantOpenForOrdering = acceptingOrders && !noSlotsAtAll;

  const slotsToShow = isNextDay ? (nextDay?.slots || []) : todaySlots;

  const canConfirm =
    restaurantOpenForOrdering &&
    !!scheduledTime &&
    (orderType === "pickup" || (orderType === "delivery" && !!postalEntry));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        data-testid="order-context-dialog"
        className="max-w-md w-[calc(100vw-1rem)] sm:w-full p-0 overflow-hidden max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] flex flex-col gap-0"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="bg-[#0A0A0A] text-white px-6 py-5 flex-shrink-0">
          <DialogTitle asChild>
            <h2
              className="text-2xl font-black tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Comment commander ?
            </h2>
          </DialogTitle>
          <p className="text-xs uppercase tracking-[0.2em] text-neutral-400 mt-1">
            Choisissez vos préférences
          </p>
        </div>

        <div className="px-6 py-5 space-y-5 flex-1 overflow-y-auto min-h-0">
          {!acceptingOrders && (
            <div
              data-testid="closed-warning"
              className="text-sm bg-amber-50 border border-amber-300 text-amber-900 rounded-md px-3 py-2 flex items-start gap-2"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-bold">Commandes désactivées</div>
                <div className="text-xs">Le restaurant n&apos;accepte pas de commandes pour le moment.</div>
              </div>
            </div>
          )}

          {acceptingOrders && noSlotsAtAll && (
            <div
              data-testid="no-slots-warning"
              className="text-sm bg-amber-50 border border-amber-300 text-amber-900 rounded-md px-3 py-2 flex items-start gap-2"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-bold">Restaurant fermé</div>
                <div className="text-xs">Aucun créneau disponible aujourd&apos;hui ni dans les prochains jours.</div>
              </div>
            </div>
          )}

          {acceptingOrders && !hasToday && hasNextDay && (
            <div
              data-testid="next-day-info"
              className="text-sm bg-[#E9C46A]/20 border-2 border-[#E9C46A] text-neutral-900 rounded-md px-3 py-3 flex items-start gap-2"
            >
              <CalendarClock className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#D90429]" />
              <div>
                <div className="font-black uppercase tracking-wider text-xs">Aujourd&apos;hui complet</div>
                <div className="text-xs mt-0.5">
                  Commandez dès maintenant pour <strong>{formatDateLabel(nextDay.date)}</strong>.
                </div>
              </div>
            </div>
          )}

          {/* Type */}
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 block mb-2">
              Mode
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="ctx-delivery-btn"
                onClick={() => setOrderType("delivery")}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-md border-2 transition-all ${
                  orderType === "delivery"
                    ? "border-[#D90429] bg-[#D90429]/5"
                    : "border-neutral-200 hover:border-neutral-400"
                }`}
              >
                <Truck className="w-5 h-5" />
                <span className="text-sm font-bold">Livraison</span>
              </button>
              <button
                data-testid="ctx-pickup-btn"
                onClick={() => setOrderType("pickup")}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-md border-2 transition-all ${
                  orderType === "pickup"
                    ? "border-[#D90429] bg-[#D90429]/5"
                    : "border-neutral-200 hover:border-neutral-400"
                }`}
              >
                <ShoppingBag className="w-5 h-5" />
                <span className="text-sm font-bold">À emporter</span>
              </button>
            </div>
          </div>

          {/* Pickup address callout */}
          {orderType === "pickup" && (
            <div
              data-testid="ctx-pickup-address"
              className="flex items-start gap-2 bg-[#E9C46A]/20 border-2 border-[#E9C46A] rounded-md px-3 py-3"
            >
              <AlertTriangle className="w-5 h-5 text-[#9b6f10] mt-0.5 flex-shrink-0" />
              <div className="text-xs leading-snug">
                <div className="font-black uppercase tracking-wider text-[11px] text-[#9b6f10] mb-1">
                  Attention · Nous avons changé d&apos;adresse !
                </div>
                <div className="text-neutral-900 font-bold text-sm">
                  Rue Pré-du-Marché 15, 1004 Lausanne
                </div>
                <div className="text-[11px] text-neutral-700 mt-0.5">
                  Merci de bien noter notre nouvelle adresse avant de venir récupérer votre commande.
                </div>
              </div>
            </div>
          )}

          {/* Postal code */}
          {orderType === "delivery" && (
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> Code postal
              </label>
              <Input
                data-testid="ctx-postal-input"
                value={postalCode}
                onChange={(e) =>
                  setPostalCode(e.target.value.replace(/\D/g, "").slice(0, 4))
                }
                placeholder="Ex: 1018"
                className="h-11 border-neutral-300 focus-visible:ring-[#D90429]"
                maxLength={4}
              />
              {postalCode && postalCode.length === 4 && settings && postalEntry && (
                <div
                  data-testid="ctx-fee-info"
                  className="mt-2 text-xs flex items-center justify-between bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2"
                >
                  <span className="text-neutral-600">
                    Min. <b>{minOrder.toFixed(2)} CHF</b>
                  </span>
                  <span className="text-neutral-600">
                    Frais <b>{Number(deliveryFee).toFixed(2)} CHF</b>
                  </span>
                </div>
              )}
              {postalInvalid && (
                <div
                  data-testid="ctx-postal-invalid"
                  className="mt-2 text-xs bg-red-50 border border-red-200 text-[#BF0424] rounded-md px-3 py-2"
                >
                  Le code postal n&apos;est pas livrable. En cas de doute, merci de nous appeler directement au <strong>078 616 29 29</strong>.
                </div>
              )}
            </div>
          )}

          {/* Time slot */}
          {restaurantOpenForOrdering && (
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Quand ?
              </label>

              {/* Day toggle if both today/next day are available */}
              {hasToday && hasNextDay && (
                <div className="flex gap-2 mb-2" data-testid="day-toggle">
                  <button
                    data-testid="day-toggle-today"
                    onClick={() => {
                      setIsNextDay(false);
                      setScheduledDate("");
                      setScheduledTime(todaySlots[0]);
                    }}
                    className={`flex-1 px-3 py-2 rounded-md text-xs font-bold border ${
                      !isNextDay
                        ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                        : "bg-white text-neutral-700 border-neutral-300"
                    }`}
                  >
                    Aujourd&apos;hui
                  </button>
                  <button
                    data-testid="day-toggle-nextday"
                    onClick={() => {
                      setIsNextDay(true);
                      setScheduledDate(nextDay.date);
                      setScheduledTime(nextDay.slots[0]);
                    }}
                    className={`flex-1 px-3 py-2 rounded-md text-xs font-bold border ${
                      isNextDay
                        ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                        : "bg-white text-neutral-700 border-neutral-300"
                    }`}
                  >
                    {formatDateLabel(nextDay.date)}
                  </button>
                </div>
              )}

              {loadingSlots ? (
                <div className="text-sm text-neutral-500">Chargement des créneaux…</div>
              ) : slotsToShow.length === 0 ? (
                <div
                  data-testid="ctx-no-slots"
                  className="text-sm text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2"
                >
                  Aucun créneau disponible.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                  {slotsToShow.map((s) => (
                    <button
                      key={s}
                      data-testid={`ctx-slot-${s}`}
                      onClick={() => setScheduledTime(s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                        scheduledTime === s
                          ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                          : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900"
                      }`}
                    >
                      {s === "asap" ? "Dès que possible" : s}
                    </button>
                  ))}
                </div>
              )}

              {isNextDay && scheduledTime && (
                <p className="text-[11px] text-neutral-600 mt-2" data-testid="next-day-confirm">
                  Commande pour <strong>{formatDateLabel(scheduledDate || nextDay?.date)}</strong> à <strong>{scheduledTime}</strong>.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-neutral-200 px-6 py-4 bg-neutral-50 flex-shrink-0">
          <Button
            data-testid="ctx-confirm-btn"
            disabled={!canConfirm}
            onClick={() => onConfirm({ orderType, postalCode, scheduledTime, scheduledDate, isNextDay })}
            className="w-full bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-11 font-bold tracking-wide disabled:opacity-40"
          >
            Voir le menu
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderContextDialog;
